import type { ResponseReadAll } from "epsm-api-types";
import { Converter } from "epsm-api-types/converter";
import {
	type PriceDaily,
	type PriceDailyDTO,
	PriceDailyTypes,
	type PriceIntraday,
} from "epsm-api-types/models";
import type { PriceInfo as PriceInfoTypes } from "epsm-api-types/ws";
import type { UTCTimestamp } from "lightweight-charts";
import { BaseService } from "@/services/BaseService";
import type { IReadAllParams } from "@/services/RequestParams";
import {
	HttpClient,
	type IHttpClient,
} from "../../../../../services/HttpClient";
import { Timeframes } from "../../types";

export type PriceInfo = PriceInfoTypes<UTCTimestamp>;

const FIELDS_NON_INTRADAY = [
	"date",
	"adj_open",
	"adj_high",
	"adj_low",
	"adj_close",
	"adj_volume",
];
const FIELDS_INTRADAY = ["date", "open", "high", "low", "close", "volume"];

export interface TimeframeInfo {
	fields: string[];
	requestPath: string;
	wsType: string;
	duration: number;
	isIntraday: boolean;
}

export const priceMapper = (isIntraday: boolean) => {
	return isIntraday ? mapIntraday : mapNonIntraday;
};

const mapNonIntraday = (price: PriceDaily) =>
	({
		time: price.date?.getTime() as UTCTimestamp,
		open: price.adj_open,
		close: price.adj_close,
		high: price.adj_high,
		low: price.adj_low,
		volume: price.adj_volume,
	}) as PriceInfo;

const mapIntraday = (price: PriceIntraday) =>
	({
		time: price.date?.getTime() as UTCTimestamp,
		open: price.open,
		close: price.close,
		high: price.high,
		low: price.low,
		volume: price.volume,
	}) as PriceInfo;

const DURATION_MIN_IN_MILLISECONDS = 60 * 1000;

export const TIMEFRAME_MAP: Record<Timeframes, TimeframeInfo> = {
	[Timeframes.ONE_MIN]: {
		fields: FIELDS_INTRADAY,
		requestPath: "intraday/1m",
		isIntraday: true,
		wsType: "chart_1min",
		duration: DURATION_MIN_IN_MILLISECONDS,
	},
	[Timeframes.FIVE_MIN]: {
		fields: FIELDS_INTRADAY,
		requestPath: "intraday/5m",
		isIntraday: true,
		wsType: "chart_5min",
		duration: 5 * DURATION_MIN_IN_MILLISECONDS,
	},
	[Timeframes.FIFTEEN_MIN]: {
		fields: FIELDS_INTRADAY,
		requestPath: "intraday/15m",
		isIntraday: true,
		wsType: "chart_15min",
		duration: 15 * DURATION_MIN_IN_MILLISECONDS,
	},
	[Timeframes.ONE_HOUR]: {
		fields: FIELDS_INTRADAY,
		requestPath: "intraday/1h",
		isIntraday: true,
		wsType: "chart_1h",
		duration: 60 * DURATION_MIN_IN_MILLISECONDS,
	},
	[Timeframes.ONE_DAY]: {
		fields: FIELDS_NON_INTRADAY,
		requestPath: "daily",
		isIntraday: false,
		wsType: "chart_1d",
		duration: 24 * 60 * DURATION_MIN_IN_MILLISECONDS,
	},
	[Timeframes.ONE_WEEK]: {
		fields: FIELDS_NON_INTRADAY,
		requestPath: "weekly",
		isIntraday: false,
		wsType: "chart_1w",
		duration: 7 * 24 * 60 * DURATION_MIN_IN_MILLISECONDS,
	},
};

export class PriceService extends BaseService<PriceDaily, PriceDailyDTO> {
	private constructor(private httpClient: IHttpClient) {
		super(new Converter(PriceDailyTypes));
	}

	private static instance: PriceService;

	static create() {
		if (!PriceService.instance) {
			PriceService.instance = new PriceService(HttpClient.create());
		}
		return PriceService.instance;
	}

	async getData(
		params: Readonly<IReadAllParams>,
		timeFrame: Timeframes,
		signal?: AbortSignal,
	): Promise<ResponseReadAll<PriceInfo>> {
		const response = await this.httpClient.get<ResponseReadAll<PriceDailyDTO>>(
			`/prices/${TIMEFRAME_MAP[timeFrame].requestPath}`,
			{
				params: this.getQueryParams({
					fields: TIMEFRAME_MAP[timeFrame].fields,
					types: { date: "num" }, // TODO: FIX api and use number
					...params,
				}),
				signal,
			},
		);

		const mapper = priceMapper(TIMEFRAME_MAP[timeFrame].isIntraday);
		return this.getResponseReadAll(response, params.types, mapper);
	}
}
